import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import axiosInstance from '../api';
import i18n from '../../i18n';
import PolicyTemplatesModal from './PolicyTemplatesModal';
import PolicyEditorModal from './PolicyEditorModal';
import './Policies.css';

const FRAMEWORK_COLORS = [
  '#1e3a5f','#0e7490','#065f46','#6d28d9','#b45309','#be185d','#1d4ed8','#047857',
];

function frameworkColor(idx) {
  return FRAMEWORK_COLORS[idx % FRAMEWORK_COLORS.length];
}

const STATUS_META = {
  PUBLISHED:    { labelKey: 'PoliciesPage.statusPublished',   bg: '#f0fdf4', color: '#166534' },
  ACTIVE:       { labelKey: 'PoliciesPage.statusActive',      bg: '#eff6ff', color: '#1d4ed8' },
  DRAFT:        { labelKey: 'PoliciesPage.statusDraft',       bg: '#f8fafc', color: '#64748b' },
  UNDER_REVIEW: { labelKey: 'PoliciesPage.statusUnderReview', bg: '#fff7ed', color: '#b45309' },
};

function statusBadge(status) {
  const meta = STATUS_META[status];
  const m = meta
    ? { label: i18n.t(meta.labelKey), bg: meta.bg, color: meta.color }
    : { label: status || '—', bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
      borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: m.bg, color: m.color,
    }}>
      {m.label}
    </span>
  );
}

/* ── Org Policies Tab ──────────────────────────────────────────────── */
function OrgPoliciesTab({ onRefresh }) {
  const { t } = useTranslation();
  const [orgPolicies,  setOrgPolicies]  = useState([]);
  const [policyDocs,   setPolicyDocs]   = useState([]); // uploaded docs categorised as Policies
  const navigate = useNavigate();
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [publishing,   setPublishing]   = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const [ackData,      setAckData]      = useState({});   // orgPolicyId → { total, signed }
  const [publishConfirm, setPublishConfirm] = useState(null);
  const [search,       setSearch]       = useState('');
  const [downloading,  setDownloading]  = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null); // T-0389 Phase 2: HTML editor modal target

  const role = localStorage.getItem('role');
  const orgRole = localStorage.getItem('orgRole');
  const isCpa = orgRole === 'CPA_ADMIN' || orgRole === 'CISO_RESELLER';
  // Org admins log in with role ROLE_USER + orgRole ORG_ADMIN; the backend (assertOrgManager) allows them.
  const isOrgManager = role === 'ROLE_ADMIN' || orgRole === 'ORG_ADMIN';
  const [partnerPolAvail, setPartnerPolAvail] = useState([]);
  const [authoring, setAuthoring] = useState(false);
  const [authorForm, setAuthorForm] = useState({ name: '', description: '', frameworkId: '' });
  const [pfw, setPfw] = useState([]); // frameworks for the author form
  const [adopting, setAdopting] = useState(null);

  const loadOrgPolicies = () => {
    setLoading(true);
    axiosInstance.get('/org-documents')
      .then(res => setPolicyDocs((Array.isArray(res.data) ? res.data : []).filter(d => (d.category || '').toLowerCase() === 'policies')))
      .catch(() => {});
    axiosInstance.get('/organization-policies?page=0&size=50')
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : [];
        setOrgPolicies(arr);
        // Load acknowledgement data for published and active policies
        const withAcks = arr.filter(p => ['PUBLISHED', 'ACTIVE'].includes(p.policyStatus));
        withAcks.forEach(p => {
          axiosInstance.get(`/organization-policies/${p.id}/acknowledgements`)
            .then(r => setAckData(prev => ({ ...prev, [p.id]: r.data })))
            .catch(() => {});
        });
      })
      .catch(() => setError(t('PoliciesPage.errorLoadOrgPolicies')))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadOrgPolicies(); }, []);

  const handlePublish = async (policyId) => {
    setPublishing(policyId);
    setPublishConfirm(null);

    // Step 1: publish — this is the critical call
    try {
      await axiosInstance.post(`/organization-policies/${policyId}/publish`);
    } catch {
      alert(t('PoliciesPage.alertPublishFailed'));
      setPublishing(null);
      return;
    }

    // Step 2: refresh list (non-critical — failure just means user sees stale data)
    try {
      const res = await axiosInstance.get('/organization-policies?page=0&size=50');
      const arr = Array.isArray(res.data) ? res.data : [];
      setOrgPolicies(arr);
    } catch { /* ignore refresh failure */ }

    // Step 3: load acknowledgement data for the published policy
    try {
      const ackRes = await axiosInstance.get(`/organization-policies/${policyId}/acknowledgements`);
      setAckData(prev => ({ ...prev, [policyId]: ackRes.data }));
    } catch { /* ignore ack load failure */ }

    setPublishing(null);
  };

  // Bulk-publish every adopted-but-unpublished policy at once (calls the governance endpoint).
  const handlePublishAll = async () => {
    setPublishingAll(true);
    try {
      await axiosInstance.post('/audit/governance/publish-policies');
      const res = await axiosInstance.get('/organization-policies?page=0&size=50');
      setOrgPolicies(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert(t('PoliciesPage.alertPublishFailed'));
    } finally {
      setPublishingAll(false);
    }
  };

  // Downloads THIS org's own adopted copy (org-scoped endpoint) -- never the shared template file
  // PolicyFilesTab's handleDownload hits, which is served identically to every org that adopted it.
  const handleDownloadOrgPolicy = async (policy, e) => {
    e.stopPropagation();
    setDownloading(policy.id);
    try {
      const res = await axiosInstance.get(`/organization-policies/${policy.id}/download`, {
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      // policy.policyLink is the shared template's link, not this org's scoped copy -- close enough to
      // borrow just the file extension from (same source file), but NOT safe to use as the full filename.
      const dot  = (policy.policyLink || '').lastIndexOf('.');
      const ext  = dot > -1 ? policy.policyLink.slice(dot) : '';
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `${policy.policyName || 'policy'}${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert(t('PoliciesPage.alertDownloadFailed'));
    } finally {
      setDownloading(null);
    }
  };

  // T-0389 Phase 2: merge the saved contentHtml back into local state (avoids a full refetch).
  const handleDraftSaved = (updatedDto) => {
    setOrgPolicies(prev => prev.map(p => (p.id === updatedDto.id ? updatedDto : p)));
  };

  const unpublishedCount = orgPolicies.filter(p => p.policyStatus !== 'PUBLISHED').length;

  const displayed = orgPolicies.filter(p =>
    !search ||
    (p.policyName || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.policyStatus || '').toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    axiosInstance.get('/partner-library/policies/available').then(r => setPartnerPolAvail(r.data || [])).catch(() => {});
    if (isCpa) axiosInstance.get('/frameworks').then(r => setPfw(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitAuthorPolicy = async () => {
    if (!authorForm.name.trim() || !authorForm.frameworkId) {
      alert(t('PoliciesPage.authorValidation', 'Name and framework are required.'));
      return;
    }
    try {
      await axiosInstance.post('/partner-library/policies', {
        name: authorForm.name, description: authorForm.description, frameworkId: Number(authorForm.frameworkId),
      });
      setAuthoring(false); setAuthorForm({ name: '', description: '', frameworkId: '' });
      alert(t('PoliciesPage.authored', 'Library policy created. Your managed clients can now adopt it.'));
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data || t('PoliciesPage.authorFailed', 'Could not create the policy.'));
    }
  };

  const adoptPartnerPolicy = async (pol) => {
    setAdopting(pol.id);
    try {
      await axiosInstance.post(`/partner-library/policies/${pol.id}/adopt`);
      const [avail] = await Promise.all([
        axiosInstance.get('/partner-library/policies/available'),
        loadOrgPolicies(),
      ]);
      setPartnerPolAvail(avail.data || []);
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data || t('PoliciesPage.adoptFailed', 'Could not adopt this policy.'));
    } finally { setAdopting(null); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
      <div className="pol-spinner" />
    </div>
  );

  if (error) return (
    <div className="pol-error">
      <i className="fas fa-exclamation-circle" /> {error}
      <button className="pol-retry" onClick={loadOrgPolicies}>{t('PoliciesPage.retry')}</button>
    </div>
  );

  return (
    <div>
      {/* Sub-toolbar */}
      <div className="pol-toolbar" style={{ marginBottom: 20 }}>
        <div className="pol-search-wrap">
          <i className="fas fa-search pol-search-icon" />
          <input
            className="pol-search"
            placeholder={t('PoliciesPage.searchOrgPoliciesPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          className="pol-upload-btn"
          style={{ background: '#6d28d9' }}
          onClick={() => setShowTemplates(true)}
        >
          <i className="fas fa-layer-group" /> {t('PoliciesPage.templateLibrary')}
        </button>
        {isCpa && (
          <button className="pol-upload-btn" style={{ background: '#6b21a8' }} onClick={() => setAuthoring(true)}>
            <i className="fas fa-book" /> {t('PoliciesPage.authorLibraryPolicy', 'Author library policy')}
          </button>
        )}
        {isOrgManager && unpublishedCount > 0 && (
          <button
            className="pol-upload-btn"
            style={{ background: '#1d4ed8' }}
            disabled={publishingAll}
            onClick={handlePublishAll}
            title={t('PoliciesPage.publishAllTitle', 'Publish all adopted-but-unpublished policies')}
          >
            <i className={`fas ${publishingAll ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />{' '}
            {publishingAll
              ? t('PoliciesPage.publishing', 'Publishing…')
              : t('PoliciesPage.publishAll', { defaultValue: 'Publish all ({{count}})', count: unpublishedCount })}
          </button>
        )}
      </div>

      {/* Stat bar */}
      <div className="pol-stat-bar" style={{ marginBottom: 20 }}>
        <div className="pol-stat">
          <span className="pol-stat-val">{orgPolicies.length}</span>
          <span className="pol-stat-label">{t('PoliciesPage.statTotal')}</span>
        </div>
        <div className="pol-stat">
          <span className="pol-stat-val" style={{ color: '#166534' }}>
            {orgPolicies.filter(p => p.policyStatus === 'PUBLISHED').length}
          </span>
          <span className="pol-stat-label">{t('PoliciesPage.statPublished')}</span>
        </div>
        <div className="pol-stat">
          <span className="pol-stat-val" style={{ color: '#64748b' }}>
            {orgPolicies.filter(p => p.policyStatus === 'DRAFT').length}
          </span>
          <span className="pol-stat-label">{t('PoliciesPage.statDrafts')}</span>
        </div>
      </div>

      {partnerPolAvail && partnerPolAvail.length > 0 && (
        <div className="pol-partner-avail" style={{ marginBottom: 20 }}>
          <div className="pol-partner-hd">
            <i className="fas fa-book" /> {t('PoliciesPage.availableFromPartner', 'Available from your compliance partner')}
            <span className="pol-partner-count">{partnerPolAvail.filter(p => !p.adopted).length}</span>
          </div>
          {partnerPolAvail.map(pol => (
            <div key={pol.id} className="pol-partner-item">
              <div className="pol-partner-meta">
                <span className="pol-partner-name">{pol.name}</span>
                {pol.description && <span className="pol-partner-desc">{pol.description}</span>}
              </div>
              {pol.adopted
                ? <span className="pol-partner-adopted"><i className="fas fa-check" /> {t('PoliciesPage.adopted', 'Adopted')}</span>
                : <button className="pol-upload-btn" style={{ padding: '6px 14px', fontSize: 12 }} disabled={adopting === pol.id}
                          onClick={() => adoptPartnerPolicy(pol)}>
                    <i className={`fas ${adopting === pol.id ? 'fa-spinner fa-spin' : 'fa-plus'}`} /> {t('PoliciesPage.adopt', 'Adopt')}
                  </button>}
            </div>
          ))}
        </div>
      )}

      {authoring && (
        <div className="pol-modal-overlay" onClick={() => setAuthoring(false)}>
          <div className="pol-modal" onClick={e => e.stopPropagation()}>
            <div className="pol-modal-hd">
              <span>{t('PoliciesPage.authorLibraryPolicy', 'Author library policy')}</span>
              <button className="pol-modal-close" onClick={() => setAuthoring(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="pol-modal-body">
              <p className="pol-modal-hint">{t('PoliciesPage.authorHint', 'This library policy is owned by your firm and offered to your managed clients to adopt — it never enters the global template library.')}</p>
              <label className="pol-field-label">{t('PoliciesPage.nameLabel', 'Policy name')}</label>
              <input className="pol-input" value={authorForm.name}
                onChange={e => setAuthorForm(f => ({ ...f, name: e.target.value }))} />
              <label className="pol-field-label">{t('PoliciesPage.descLabel', 'Description')}</label>
              <textarea className="pol-input" rows={3} value={authorForm.description}
                onChange={e => setAuthorForm(f => ({ ...f, description: e.target.value }))} />
              <label className="pol-field-label">{t('PoliciesPage.frameworkLabel', 'Framework')}</label>
              <select className="pol-input" value={authorForm.frameworkId}
                onChange={e => setAuthorForm(f => ({ ...f, frameworkId: e.target.value }))}>
                <option value="">{t('PoliciesPage.selectFramework', 'Select a framework')}</option>
                {pfw.map(fw => <option key={fw.id} value={fw.id}>{fw.displayName || fw.name}</option>)}
              </select>
            </div>
            <div className="pol-modal-ft">
              <button className="pol-btn-secondary" onClick={() => setAuthoring(false)}>{t('PoliciesPage.cancel', 'Cancel')}</button>
              <button className="pol-btn-primary" onClick={submitAuthorPolicy}>{t('PoliciesPage.create', 'Create')}</button>
            </div>
          </div>
        </div>
      )}

      {displayed.length === 0 && policyDocs.length === 0 ? (
        <div className="pol-empty">
          <i className="fas fa-folder-open" />
          <p>{search ? t('PoliciesPage.emptyOrgNoMatch') : t('PoliciesPage.emptyOrgNoPolicies')}</p>
          <button className="pol-upload-btn" style={{ background: '#6d28d9' }} onClick={() => setShowTemplates(true)}>
            <i className="fas fa-layer-group" /> {t('PoliciesPage.openTemplateLibrary')}
          </button>
        </div>
      ) : (
        <div className="pol-grid">
          {displayed.map(policy => {
            const ack = ackData[policy.id];
            // Any policy that isn't already published can be published — including auto-adopted
            // ones whose status is null/empty (previously these showed no Publish button).
            const canPublish  = policy.policyStatus !== 'PUBLISHED';
            const progress    = ack ? Math.round((ack.signed / Math.max(ack.total, 1)) * 100) : 0;

            return (
              <div key={policy.id} className="pol-card" style={{ cursor: 'default' }}>
                <div className="pol-card-icon">
                  <i className="fas fa-file-contract" />
                </div>
                <div className="pol-card-body" style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div className="pol-card-title">{policy.policyName}</div>
                    {statusBadge(policy.policyStatus)}
                  </div>
                  {policy.policyDescription && (
                    <p className="pol-card-desc">{policy.policyDescription}</p>
                  )}

                  {/* Signature progress (published/active policies with acknowledgements) */}
                  {ack && ack.total > 0 && (
                    <div className="org-pol-sig-wrap">
                      <div className="org-pol-sig-label">
                        <span><i className="fas fa-pen-nib" style={{ marginRight: 6, color: '#6d28d9' }} />
                          {t('PoliciesPage.signedProgress', { signed: ack.signed, total: ack.total })}
                        </span>
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{progress}%</span>
                      </div>
                      <div className="org-pol-progress-track">
                        <div
                          className="org-pol-progress-fill"
                          style={{
                            width: `${progress}%`,
                            background: progress === 100 ? '#16a34a' : progress >= 50 ? '#1d4ed8' : '#f59e0b',
                          }}
                        />
                      </div>
                      {ack.pending > 0 && (
                        <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                          <i className="fas fa-clock" style={{ marginRight: 4 }} />
                          {t('PoliciesPage.pendingSignatures', { count: ack.pending })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Framework badges */}
                  {(policy.frameworks || []).length > 0 && (
                    <div className="pol-card-frameworks" style={{ marginTop: 8 }}>
                      {policy.frameworks.slice(0, 3).map((fw, i) => (
                        <span key={fw.id || i} className="pol-fw-badge" style={{ background: frameworkColor(i) }}>
                          {fw.displayName || fw.name || fw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="pol-card-actions" style={{ flexDirection: 'column', gap: 8 }}>
                  {canPublish && isOrgManager && (
                    <button
                      className="pol-download-btn"
                      style={{ background: '#1d4ed8', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', width: 90 }}
                      disabled={publishing === policy.id}
                      onClick={() => setPublishConfirm(policy)}
                    >
                      {publishing === policy.id
                        ? <><i className="fas fa-spinner fa-spin" /></>
                        : <><i className="fas fa-paper-plane" /> {t('PoliciesPage.publish')}</>}
                    </button>
                  )}
                  <button
                    className="pol-download-btn"
                    title={t('PoliciesPage.downloadPolicyFileTitle')}
                    disabled={downloading === policy.id}
                    onClick={e => handleDownloadOrgPolicy(policy, e)}
                  >
                    {downloading === policy.id
                      ? <i className="fas fa-spinner fa-spin" />
                      : <i className="fas fa-download" />}
                  </button>
                  {/* T-0389 Phase 2: only policies migrated to HTML storage (contentHtml populated at
                      adoption time by PolicyTokenService) get in-app editing; others still fall back to
                      the .docx download-only flow until Phase 3's template migration reaches them. */}
                  {isOrgManager && policy.contentHtml != null && (
                    <button
                      className="pol-download-btn"
                      style={{ background: '#475569', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', width: 90 }}
                      onClick={() => setEditingPolicy(policy)}
                    >
                      <i className="fas fa-pen" /> {t('PoliciesPage.editPolicy')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {policyDocs.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#334155', margin: '0 0 12px' }}>
            <i className="fas fa-file-lines" style={{ marginRight: 8 }} />
            {t('PoliciesPage.uploadedPolicyDocs', 'Uploaded policy documents')}
          </h3>
          <div className="pol-grid">
            {policyDocs.map(d => (
              <div key={`doc-${d.id}`} className="pol-card" style={{ cursor: 'pointer' }}
                onClick={() => navigate((role === 'ROLE_PLATFORM_ADMIN' ? '/adminHome' : '/dashboard') + '/org-documents/' + d.id)}>
                <div className="pol-card-icon"><i className="fas fa-file-lines" /></div>
                <div className="pol-card-body" style={{ flex: 1 }}>
                  <div className="pol-card-title">{d.title}</div>
                  <div style={{ marginTop: 6 }}>{statusBadge(d.workflowStatus === 'IN_FORCE' ? 'PUBLISHED' : 'DRAFT')}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    {t('PoliciesPage.uploadedDocHint', 'Uploaded document · click to open')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Publish confirmation modal */}
      {publishConfirm && (
        <div className="pol-overlay" onClick={() => setPublishConfirm(null)}>
          <div className="pol-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="pol-modal-header">
              <span><i className="fas fa-paper-plane" /> {t('PoliciesPage.publishPolicy')}</span>
              <button className="pol-modal-close" onClick={() => setPublishConfirm(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="pol-modal-body">
              <p style={{ margin: 0, fontSize: 14, color: '#334155' }}>
                <Trans i18nKey="PoliciesPage.publishConfirmIntro" values={{ name: publishConfirm.policyName }}>
                  Publishing <strong>{{ name: publishConfirm.policyName }}</strong> will:
                </Trans>
              </p>
              <ul style={{ marginTop: 12, paddingLeft: 20, fontSize: 14, color: '#475569', lineHeight: 1.8 }}>
                <li>
                  <Trans i18nKey="PoliciesPage.publishConfirmStatus">
                    Change status to <strong>PUBLISHED</strong>
                  </Trans>
                </li>
                <li>{t('PoliciesPage.publishConfirmRequests')}</li>
                <li>{t('PoliciesPage.publishConfirmWindow')}</li>
              </ul>
              <p style={{ margin: '12px 0 0', fontSize: 13, color: '#64748b' }}>
                {t('PoliciesPage.cannotBeUndone')}
              </p>
            </div>
            <div className="pol-modal-footer">
              <button className="pol-btn-secondary" onClick={() => setPublishConfirm(null)}>{t('PoliciesPage.cancel')}</button>
              <button
                className="pol-btn-primary"
                disabled={publishing === publishConfirm.id}
                onClick={() => handlePublish(publishConfirm.id)}
              >
                {publishing === publishConfirm.id
                  ? <><i className="fas fa-spinner fa-spin" /> {t('PoliciesPage.publishing')}</>
                  : <><i className="fas fa-paper-plane" /> {t('PoliciesPage.publishPolicy')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates modal */}
      {showTemplates && (
        <PolicyTemplatesModal
          onClose={() => setShowTemplates(false)}
          onUse={() => { loadOrgPolicies(); }}
        />
      )}

      {/* T-0389 Phase 2: HTML editor modal */}
      {editingPolicy && (
        <PolicyEditorModal
          policy={editingPolicy}
          onClose={() => setEditingPolicy(null)}
          onSaved={handleDraftSaved}
        />
      )}
    </div>
  );
}

/* ── Policy Files Tab (original) ───────────────────────────────────── */
function PolicyFilesTab({ onGoToOrgPolicies }) {
  const { t } = useTranslation();
  const [policies,    setPolicies]    = useState([]);
  const [frameworks,  setFrameworks]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState(null);
  const [showUpload,  setShowUpload]  = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [deleting,    setDeleting]    = useState(null);
  const [adoption,    setAdoption]    = useState({}); // policyId -> { orgPolicyId, status }
  const [adoptionLoaded, setAdoptionLoaded] = useState(false);
  const [adoptionFailed, setAdoptionFailed] = useState(false);
  const [ackData,     setAckData]     = useState({}); // orgPolicyId -> { total, signed, pending }
  const [form, setForm] = useState({
    policyName: '', description: '', frameworkIds: [], controlIds: [],
  });
  const [formFile, setFormFile] = useState(null);
  const fileRef = useRef(null);

  const role    = localStorage.getItem('role');
  const orgRole = localStorage.getItem('orgRole');
  const orgId   = Number(localStorage.getItem('orgId'));

  useEffect(() => {
    Promise.all([
      axiosInstance.get('/policies'),
      axiosInstance.get('/frameworks'),
    ])
      .then(([polRes, fwRes]) => {
        setPolicies(Array.isArray(polRes.data) ? polRes.data : []);
        setFrameworks(Array.isArray(fwRes.data) ? fwRes.data : []);
      })
      .catch(() => setError(t('PoliciesPage.errorLoadPolicies')))
      .finally(() => setLoading(false));

    // Best-effort: map each file to this org's adoption/publish status (if adopted) so the
    // Policy Files tab isn't a dead-end for the ack/publish state that lives on the Org Policies tab.
    axiosInstance.get('/organization-policies?page=0&size=200')
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : (res.data?.content || []);
        const map = {};
        arr.forEach(op => {
          if (op.policyId != null) map[op.policyId] = { orgPolicyId: op.id, status: op.policyStatus };
        });
        setAdoption(map);
      })
      .catch(() => setAdoptionFailed(true))
      .finally(() => setAdoptionLoaded(true));
  }, [t]);

  const loadAck = (orgPolicyId) => {
    if (ackData[orgPolicyId]) return;
    axiosInstance.get(`/organization-policies/${orgPolicyId}/acknowledgements`)
      .then(r => setAckData(prev => ({ ...prev, [orgPolicyId]: r.data })))
      .catch(() => {});
  };

  const frameworkName = (id) => {
    const fw = frameworks.find(f => f.id === id || f.id === Number(id));
    return fw ? (fw.displayName || fw.name || id) : id;
  };

  const displayed = policies.filter(p =>
    !search ||
    (p.policyName || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleDownload = async (policy, e) => {
    e.stopPropagation();
    setDownloading(policy.policyId);
    try {
      const res = await axiosInstance.get(`/policies/${policy.policyId}/download`, {
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', policy.policyFileName || 'policy-file');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert(t('PoliciesPage.alertDownloadFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const canDelete = (policy) => {
    if (role === 'ROLE_ADMIN') return true;
    if (orgRole === 'ORG_ADMIN' && policy.ownerOrgId && policy.ownerOrgId === orgId) return true;
    return false;
  };

  const handleDelete = async (policy, e) => {
    e.stopPropagation();
    if (!window.confirm(t('PoliciesPage.confirmDelete', { name: policy.policyName }))) return;
    setDeleting(policy.policyId);
    try {
      await axiosInstance.delete(`/policies/${policy.policyId}`);
      setPolicies(prev => prev.filter(p => p.policyId !== policy.policyId));
      if (selected?.policyId === policy.policyId) setSelected(null);
    } catch {
      alert(t('PoliciesPage.alertDeleteFailed'));
    } finally {
      setDeleting(null);
    }
  };

  const handleUpload = async () => {
    if (!form.policyName.trim()) { alert(t('PoliciesPage.alertNameRequired')); return; }
    if (!formFile)               { alert(t('PoliciesPage.alertSelectFile'));   return; }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(',')[1];
      const payload = {
        policyName:    form.policyName,
        description:   form.description,
        policyFileName: formFile.name,
        policyfile:    base64,
        frameworkIds:  form.frameworkIds.map(Number),
        controlIds:    form.controlIds,
      };
      try {
        setUploading(true);
        await axiosInstance.post('/policies', payload);
        const polRes = await axiosInstance.get('/policies');
        setPolicies(Array.isArray(polRes.data) ? polRes.data : []);
        setShowUpload(false);
        setForm({ policyName: '', description: '', frameworkIds: [], controlIds: [] });
        setFormFile(null);
      } catch {
        alert(t('PoliciesPage.alertUploadFailed'));
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(formFile);
  };

  const toggleFramework = (id) => {
    setForm(f => ({
      ...f,
      frameworkIds: f.frameworkIds.includes(id)
        ? f.frameworkIds.filter(x => x !== id)
        : [...f.frameworkIds, id],
    }));
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
      <div className="pol-spinner" />
    </div>
  );
  if (error) return (
    <div className="pol-error">
      <i className="fas fa-exclamation-circle" /> {error}
      <button className="pol-retry" onClick={() => window.location.reload()}>{t('PoliciesPage.retry')}</button>
    </div>
  );

  return (
    <div>
      {/* Stats */}
      <div className="pol-stat-bar" style={{ marginBottom: 20 }}>
        <div className="pol-stat">
          <span className="pol-stat-val">{policies.length}</span>
          <span className="pol-stat-label">{t('PoliciesPage.statTotalPolicies')}</span>
        </div>
        <div className="pol-stat">
          <span className="pol-stat-val">
            {new Set(policies.flatMap(p => p.frameworkIds || [])).size}
          </span>
          <span className="pol-stat-label">{t('PoliciesPage.statFrameworksCovered')}</span>
        </div>
        <div className="pol-stat">
          <span className="pol-stat-val">
            {new Set(policies.flatMap(p => p.controlIds || [])).size}
          </span>
          <span className="pol-stat-label">{t('PoliciesPage.statControlsMapped')}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="pol-toolbar">
        <div className="pol-search-wrap">
          <i className="fas fa-search pol-search-icon" />
          <input
            className="pol-search"
            placeholder={t('PoliciesPage.searchPoliciesPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {(role === 'ROLE_ADMIN' || orgRole === 'ORG_ADMIN') && (
          <button className="pol-upload-btn" onClick={() => setShowUpload(true)}>
            <i className="fas fa-upload" /> {t('PoliciesPage.uploadPolicy')}
          </button>
        )}
      </div>

      {/* Policy cards */}
      {displayed.length === 0 ? (
        <div className="pol-empty">
          <i className="fas fa-folder-open" />
          <p>{search ? t('PoliciesPage.emptyFilesNoMatch') : t('PoliciesPage.emptyFilesNoPolicies')}</p>
          {(role === 'ROLE_ADMIN' || orgRole === 'ORG_ADMIN') && !search && (
            <button className="pol-upload-btn" onClick={() => setShowUpload(true)}>
              <i className="fas fa-upload" /> {t('PoliciesPage.uploadFirstPolicy')}
            </button>
          )}
        </div>
      ) : (
        <div className="pol-grid">
          {displayed.map(policy => (
            <div
              key={policy.policyId}
              className="pol-card"
              onClick={() => {
                const willOpen = selected?.policyId !== policy.policyId;
                setSelected(willOpen ? policy : null);
                const op = adoption[policy.policyId];
                if (willOpen && op && ['PUBLISHED', 'ACTIVE'].includes(op.status)) loadAck(op.orgPolicyId);
              }}
            >
              <div className="pol-card-icon">
                <i className="fas fa-file-alt" />
              </div>
              <div className="pol-card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="pol-card-title">{policy.policyName}</div>
                  {!adoptionLoaded ? (
                    <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8', fontSize: 12 }} />
                  ) : adoption[policy.policyId] ? (
                    statusBadge(adoption[policy.policyId].status)
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                      borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: '#f1f5f9', color: '#64748b',
                    }}>
                      {adoptionFailed
                        ? t('PoliciesPage.adoptionUnavailable', 'Adoption status unavailable')
                        : t('PoliciesPage.notAdopted', 'Not adopted')}
                    </span>
                  )}
                </div>
                {policy.description && (
                  <p className="pol-card-desc">{policy.description}</p>
                )}
                <div className="pol-card-frameworks">
                  {(policy.frameworkIds || []).slice(0, 3).map((id, i) => (
                    <span
                      key={id}
                      className="pol-fw-badge"
                      style={{ background: frameworkColor(i) }}
                    >
                      {frameworkName(id)}
                    </span>
                  ))}
                  {(policy.frameworkIds || []).length > 3 && (
                    <span className="pol-fw-badge pol-fw-more">
                      +{policy.frameworkIds.length - 3}
                    </span>
                  )}
                </div>
              </div>
              <div className="pol-card-actions">
                <button
                  className="pol-download-btn"
                  title={t('PoliciesPage.downloadPolicyFileTitle')}
                  disabled={downloading === policy.policyId}
                  onClick={e => handleDownload(policy, e)}
                >
                  {downloading === policy.policyId
                    ? <i className="fas fa-spinner fa-spin" />
                    : <i className="fas fa-download" />}
                </button>
                {canDelete(policy) && (
                  <button
                    className="pol-download-btn"
                    title={t('PoliciesPage.deletePolicyTitle')}
                    style={{ background: '#fee2e2', color: '#ef4444' }}
                    disabled={deleting === policy.policyId}
                    onClick={e => handleDelete(policy, e)}
                  >
                    {deleting === policy.policyId
                      ? <i className="fas fa-spinner fa-spin" />
                      : <i className="fas fa-trash" />}
                  </button>
                )}
                <i className={`fas fa-chevron-${selected?.policyId === policy.policyId ? 'up' : 'down'} pol-chevron`} />
              </div>

              {selected?.policyId === policy.policyId && (
                <div className="pol-detail" onClick={e => e.stopPropagation()}>
                  <div className="pol-detail-row">
                    <span className="pol-detail-label">{t('PoliciesPage.detailFile')}</span>
                    <span className="pol-detail-val">
                      <i className="fas fa-paperclip" /> {policy.policyFileName || '—'}
                    </span>
                  </div>
                  {(policy.frameworkIds || []).length > 0 && (
                    <div className="pol-detail-row">
                      <span className="pol-detail-label">{t('PoliciesPage.detailFrameworks')}</span>
                      <div className="pol-fw-list">
                        {policy.frameworkIds.map((id, i) => (
                          <span key={id} className="pol-fw-badge" style={{ background: frameworkColor(i) }}>
                            {frameworkName(id)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(policy.controlIds || []).length > 0 && (
                    <div className="pol-detail-row">
                      <span className="pol-detail-label">{t('PoliciesPage.detailControls')}</span>
                      <div className="pol-ctrl-list">
                        {policy.controlIds.map(c => (
                          <span key={c} className="pol-ctrl-badge">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pol-detail-row">
                    <span className="pol-detail-label">{t('PoliciesPage.detailAdoption', 'Adoption')}</span>
                    <span className="pol-detail-val">
                      {!adoptionLoaded ? (
                        t('PoliciesPage.adoptionLoading', 'Loading adoption status…')
                      ) : adoption[policy.policyId] ? (
                        (() => {
                          const op = adoption[policy.policyId];
                          const ack = ackData[op.orgPolicyId];
                          const ackText = ack ? ` — ${t('PoliciesPage.signedProgress', { signed: ack.signed, total: ack.total })}` : '';
                          return `${op.status}${ackText}`;
                        })()
                      ) : adoptionFailed ? (
                        t('PoliciesPage.adoptionUnavailableDetail', 'Could not load adoption status — refresh to try again')
                      ) : t('PoliciesPage.notAdoptedDetail', 'Not adopted by this organization yet')}
                      {onGoToOrgPolicies && (
                        <button
                          className="pol-detail-link"
                          style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
                          onClick={e => { e.stopPropagation(); onGoToOrgPolicies(); }}
                        >
                          {t('PoliciesPage.viewInOrgPolicies', 'View in Org Policies')}
                        </button>
                      )}
                    </span>
                  </div>
                  <button
                    className="pol-download-full-btn"
                    disabled={downloading === policy.policyId}
                    onClick={e => handleDownload(policy, e)}
                  >
                    <i className="fas fa-download" /> {t('PoliciesPage.downloadPolicyFile')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="pol-overlay" onClick={() => setShowUpload(false)}>
          <div className="pol-modal" onClick={e => e.stopPropagation()}>
            <div className="pol-modal-header">
              <span><i className="fas fa-upload" /> {t('PoliciesPage.uploadPolicy')}</span>
              <button className="pol-modal-close" onClick={() => setShowUpload(false)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="pol-modal-body">
              <div className="pol-field">
                <label>{t('PoliciesPage.labelPolicyName')}</label>
                <input
                  className="pol-input"
                  placeholder={t('PoliciesPage.policyNamePlaceholder')}
                  value={form.policyName}
                  onChange={e => setForm({ ...form, policyName: e.target.value })}
                />
              </div>
              <div className="pol-field">
                <label>{t('PoliciesPage.labelDescription')}</label>
                <textarea
                  className="pol-input pol-textarea"
                  placeholder={t('PoliciesPage.descriptionPlaceholder')}
                  rows={3}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="pol-field">
                <label>{t('PoliciesPage.labelFrameworks')}</label>
                <div className="pol-fw-checkboxes">
                  {frameworks.map((fw, i) => (
                    <label key={fw.id} className="pol-fw-check">
                      <input
                        type="checkbox"
                        checked={form.frameworkIds.includes(fw.id)}
                        onChange={() => toggleFramework(fw.id)}
                      />
                      <span style={{ color: frameworkColor(i) }}>{fw.displayName || fw.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="pol-field">
                <label>{t('PoliciesPage.labelPolicyFile')}</label>
                <div className="pol-file-zone" onClick={() => fileRef.current?.click()}>
                  <input
                    ref={fileRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={e => setFormFile(e.target.files[0] || null)}
                  />
                  {formFile
                    ? <span className="pol-file-name"><i className="fas fa-file" /> {formFile.name}</span>
                    : <span className="pol-file-placeholder">
                        <i className="fas fa-cloud-upload-alt" /> {t('PoliciesPage.filePickerPlaceholder')}
                      </span>}
                </div>
              </div>
            </div>
            <div className="pol-modal-footer">
              <button className="pol-btn-secondary" onClick={() => setShowUpload(false)}>{t('PoliciesPage.cancel')}</button>
              <button className="pol-btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? <><i className="fas fa-spinner fa-spin" /> {t('PoliciesPage.uploading')}</> : t('PoliciesPage.uploadPolicy')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main PoliciesPage ─────────────────────────────────────────────── */
function PoliciesPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('org');

  return (
    <div className="pol-page">
      {/* Header */}
      <div className="pol-header">
        <div className="pol-header-left">
          <h2 className="pol-title">
            <i className="fas fa-file-shield" /> {t('PoliciesPage.title')}
          </h2>
          <p className="pol-subtitle">
            {t('PoliciesPage.subtitle')}
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="pol-tabs">
        <button
          className={`pol-tab${activeTab === 'org' ? ' pol-tab-active' : ''}`}
          onClick={() => setActiveTab('org')}
        >
          <i className="fas fa-building" /> {t('PoliciesPage.tabOrgPolicies')}
        </button>
        <button
          className={`pol-tab${activeTab === 'files' ? ' pol-tab-active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          <i className="fas fa-file-alt" /> {t('PoliciesPage.tabPolicyFiles')}
        </button>
      </div>

      {activeTab === 'org'   && <OrgPoliciesTab />}
      {activeTab === 'files' && <PolicyFilesTab onGoToOrgPolicies={() => setActiveTab('org')} />}
    </div>
  );
}

export default PoliciesPage;
